import { useState, useRef, useEffect, useMemo } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { useTranslation } from 'react-i18next'
import notify from 'components/atoms/Notify'
import { find, includes, toLower, split, trim, forEach, some } from 'lodash'

const CITY_NAME_TYPES = ['locality', 'administrative_area_level_2', 'administrative_area_level_1', 'sublocality', 'postal_town']
const ARABIC_SCRIPT_REGEX = /[\u0600-\u06FF]/

const hasArabicScript = value => ARABIC_SCRIPT_REGEX.test(String(value || ''))

const extractCityName = (addressComponents, { preferArabic = false } = {}) => {
  if (!addressComponents?.length) return ''

  if (preferArabic) {
    for (const type of CITY_NAME_TYPES) {
      const match = find(addressComponents, component => includes(component.types, type) && hasArabicScript(component.long_name))
      if (match?.long_name) return match.long_name
    }
  }

  for (const type of CITY_NAME_TYPES) {
    const match = find(addressComponents, component => includes(component.types, type))
    if (match?.long_name) return match.long_name
  }

  return ''
}

const extractCountryCode = addressComponents => {
  const match = find(addressComponents, component => includes(component.types, 'country'))
  return match?.short_name || ''
}

const isLocationInCountry = (addressComponents, countryCode) => {
  if (!countryCode) return true
  const locationCountryCode = extractCountryCode(addressComponents)
  return toLower(locationCountryCode) === toLower(countryCode)
}

const cityNameFromAddress = (address, { preferArabic = false } = {}) => {
  if (!address) return ''
  const parts = split(address, ',')
  if (preferArabic) {
    const arabicPart = find(parts, part => hasArabicScript(part))
    if (arabicPart) return trim(arabicPart)
  }
  return trim(parts[0]) || address
}

const isPlusCodeAddress = address => /^\s*[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(address || ''))

const pickCityName = (result, language) => {
  if (!result) return ''
  const preferArabic = language === 'ar'
  return (
    extractCityName(result.address_components, { preferArabic }) ||
    cityNameFromAddress(result.formatted_address, { preferArabic }) ||
    extractCityName(result.address_components) ||
    cityNameFromAddress(result.formatted_address)
  )
}

/** Prefer a non–Plus Code result that has a usable city name (and Arabic script when needed). */
const pickBestGeocodeResult = (results, language) => {
  if (!results?.length) return null
  const preferArabic = language === 'ar'

  if (preferArabic) {
    const withArabic = find(results, result => {
      const name = pickCityName(result, 'ar')
      return name && hasArabicScript(name)
    })
    if (withArabic) return withArabic

    for (const result of results) {
      const arabicComponent = find(
        result.address_components || [],
        component => hasArabicScript(component.long_name) && some(CITY_NAME_TYPES, type => includes(component.types, type))
      )
      if (arabicComponent) return result

      if (hasArabicScript(result.formatted_address)) return result
    }
  }

  const withoutPlusCode = find(results, result => !isPlusCodeAddress(result.formatted_address) && pickCityName(result, language))
  return withoutPlusCode || results[0]
}

const geocodeResultPayload = (result, language, status = 'OK') => {
  if (!result) {
    return { cityName: '', address: '', addressComponents: [], status }
  }
  return {
    cityName: pickCityName(result, language),
    address: result.formatted_address || '',
    addressComponents: result.address_components || [],
    status,
  }
}

/** Resolve Arabic display name from an English locality via forward geocode + Places. */
const resolveArabicCityNameFromEnglish = async (englishCity, countryName, countryCode) => {
  const queryParts = [trim(englishCity), trim(countryName)].filter(Boolean)
  if (!queryParts.length) return ''

  const query = queryParts.join(', ')
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  try {
    const params = new URLSearchParams({
      address: query,
      language: 'ar',
      key: apiKey,
    })
    if (countryCode) {
      params.set('components', `country:${toLower(countryCode)}`)
    }
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`)
    const data = await response.json()
    if (data.status === 'OK' && data.results?.length) {
      const best = pickBestGeocodeResult(data.results, 'ar')
      const cityName = pickCityName(best, 'ar')
      if (cityName && hasArabicScript(cityName)) return cityName
    }
  } catch (error) {
    console.error('Arabic forward geocode failed:', error)
  }

  try {
    await loadGoogleMapsWithLanguage('ar')

    const mapsJsName = await new Promise(resolve => {
      const geocoder = new window.google.maps.Geocoder()
      const request = { address: query }
      if (countryCode) {
        request.componentRestrictions = { country: toLower(countryCode) }
      }
      geocoder.geocode(request, (results, status) => {
        if (status === 'OK' && results?.length) {
          const best = pickBestGeocodeResult(results, 'ar')
          const cityName = pickCityName(best, 'ar')
          if (cityName && hasArabicScript(cityName)) {
            resolve(cityName)
            return
          }
        }
        resolve('')
      })
    })
    if (mapsJsName) return mapsJsName

    const placesName = await new Promise(resolve => {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'))
      service.findPlaceFromQuery({ query, fields: ['name', 'formatted_address'] }, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
          const place = results[0]
          if (hasArabicScript(place.name)) {
            resolve(place.name)
            return
          }
          if (hasArabicScript(place.formatted_address)) {
            resolve(cityNameFromAddress(place.formatted_address, { preferArabic: true }))
            return
          }
        }
        resolve('')
      })
    })
    if (placesName) return placesName

    // Text search is broader and often returns localized names for small towns.
    const textSearchName = await new Promise(resolve => {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'))
      const request = { query }
      if (countryCode) {
        request.region = toLower(countryCode)
      }
      service.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length) {
          const arabicPlace = find(results, place => hasArabicScript(place.name))
          if (arabicPlace?.name) {
            resolve(arabicPlace.name)
            return
          }
        }
        resolve('')
      })
    })
    if (textSearchName) return textSearchName
  } catch (error) {
    console.error('Arabic Places lookup failed:', error)
  }

  return ''
}

const resetGoogleMapsRuntime = () => {
  forEach(document.querySelectorAll('script[src*="maps.googleapis.com/maps/api/js"]'), script => script.remove())
  const loaderScript = document.getElementById('__googleMapsScriptId')
  if (loaderScript) loaderScript.remove()
  if (window.google) {
    delete window.google
  }
  // Allow @googlemaps/js-api-loader to be constructed again after a language switch.
  Loader.instance = undefined
  window.__ajarleeMapsLang = null
}

const loadGoogleMapsWithLanguage = language => {
  const lang = language === 'ar' ? 'ar' : 'en'
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  return new Promise((resolve, reject) => {
    if (window.google?.maps && window.__ajarleeMapsLang === lang) {
      resolve(window.google)
      return
    }

    resetGoogleMapsRuntime()

    const callbackName = `__ajarleeMapsInit_${lang}`
    window[callbackName] = () => {
      window.__ajarleeMapsLang = lang
      resolve(window.google)
      try {
        delete window[callbackName]
      } catch (error) {
        // ignore
      }
    }

    const script = document.createElement('script')
    script.id = '__googleMapsScriptId'
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=${lang}&callback=${callbackName}`
    script.onerror = () => reject(new Error('Failed to load Google Maps'))
    document.head.appendChild(script)
  })
}

const reverseGeocodeViaMapsJs = async (lat, lon, language) => {
  await loadGoogleMapsWithLanguage(language)
  return new Promise(resolve => {
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
      if (status === 'OK' && results?.length) {
        const best = pickBestGeocodeResult(results, language)
        resolve(geocodeResultPayload(best, language, status))
        return
      }
      resolve({ cityName: '', address: '', addressComponents: [], status })
    })
  })
}

const reverseGeocodeCityName = async (lat, lon, language) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  const params = new URLSearchParams({
    latlng: `${lat},${lon}`,
    language,
    key: apiKey,
  })

  try {
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`)
    const data = await response.json()

    if (data.status === 'OK' && data.results?.length) {
      const best = pickBestGeocodeResult(data.results, language)
      const cityName = pickCityName(best, language)
      // For Arabic, ignore Latin-only names so we fall back to Maps JS / Places lookup.
      if (language === 'ar' && (!cityName || !hasArabicScript(cityName))) {
        return reverseGeocodeViaMapsJs(lat, lon, language)
      }
      return geocodeResultPayload(best, language, data.status)
    }
  } catch (error) {
    console.error('REST reverse geocode failed:', error)
  }

  return reverseGeocodeViaMapsJs(lat, lon, language)
}

/**
 * City location picker for Master Data.
 * Restricts Places search to `countryCode` and rejects pins outside that country.
 * Returns English + Arabic city names for the same coordinates.
 */
const CityLocationPicker = ({ onLocationSelect, onClose, initialLat, initialLon, countryCode, language = 'en', countryName }) => {
  const { t, i18n } = useTranslation()
  const mapLanguage = language === 'ar' ? 'ar' : 'en'
  const mapT = useMemo(() => (mapLanguage === 'ar' ? i18n.getFixedT('ar') : t), [mapLanguage, i18n, t])
  const mapRef = useRef(null)
  const searchRef = useRef(null)
  const markerRef = useRef(null)

  const [address, setAddress] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [outOfCountry, setOutOfCountry] = useState(false)
  const [mapLoading, setMapLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const resolveCountryView = () =>
      new Promise(resolve => {
        if (initialLat && initialLon) {
          resolve({
            center: { lat: initialLat, lng: initialLon },
            bounds: null,
            zoom: 12,
          })
          return
        }

        if (!countryName && !countryCode) {
          resolve({
            center: { lat: 23.588, lng: 58.3829 },
            bounds: null,
            zoom: 6,
          })
          return
        }

        const geocoder = new window.google.maps.Geocoder()
        const request = {
          address: countryName || countryCode,
        }
        if (countryCode) {
          request.componentRestrictions = { country: toLower(countryCode) }
        }

        geocoder.geocode(request, (results, status) => {
          if (status === 'OK' && results[0]?.geometry) {
            const location = results[0].geometry.location
            const bounds = results[0].geometry.bounds || results[0].geometry.viewport || null
            resolve({
              center: { lat: location.lat(), lng: location.lng() },
              bounds,
              zoom: 6,
            })
            return
          }

          if (countryName && countryCode) {
            geocoder.geocode({ address: countryName }, (fallbackResults, fallbackStatus) => {
              if (fallbackStatus === 'OK' && fallbackResults[0]?.geometry) {
                const location = fallbackResults[0].geometry.location
                const bounds = fallbackResults[0].geometry.bounds || fallbackResults[0].geometry.viewport || null
                resolve({
                  center: { lat: location.lat(), lng: location.lng() },
                  bounds,
                  zoom: 6,
                })
                return
              }
              resolve({
                center: { lat: 23.588, lng: 58.3829 },
                bounds: null,
                zoom: 6,
              })
            })
            return
          }

          resolve({
            center: { lat: 23.588, lng: 58.3829 },
            bounds: null,
            zoom: 6,
          })
        })
      })

    setMapLoading(true)
    setSelectedLocation(null)
    setAddress('')
    setOutOfCountry(false)

    loadGoogleMapsWithLanguage(mapLanguage)
      .then(async () => {
        if (cancelled || !mapRef.current) return

        const countryView = await resolveCountryView()
        if (cancelled || !mapRef.current) return

        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: countryView.center,
          zoom: countryView.zoom,
          streetViewControl: false,
          fullscreenControl: false,
          ...(countryView.bounds
            ? {
                restriction: {
                  latLngBounds: countryView.bounds,
                  strictBounds: true,
                },
              }
            : {}),
        })

        if (countryView.bounds) {
          mapInstance.fitBounds(countryView.bounds)
        }

        const resolveLocation = (lat, lon, preferredAddress = '') => {
          const geocoder = new window.google.maps.Geocoder()
          geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
            if (cancelled) return

            if (status === 'OK' && results?.length) {
              const best = pickBestGeocodeResult(results, mapLanguage)
              const inCountry = isLocationInCountry(best.address_components, countryCode)
              setOutOfCountry(!inCountry)
              if (!inCountry) {
                setSelectedLocation(null)
                setAddress('')
                if (markerRef.current) {
                  markerRef.current.setMap(null)
                  markerRef.current = null
                }
                notify.error(mapT('addCityModal.messages.locationOutsideCountry'))
                return
              }
              const resolvedAddress = preferredAddress || best.formatted_address || ''
              setAddress(resolvedAddress)
              setSelectedLocation({
                lat,
                lon,
                address: resolvedAddress,
                addressComponents: best.address_components || [],
                placeName: preferredAddress || '',
              })
              return
            }

            setOutOfCountry(false)
            setAddress(preferredAddress || '')
            setSelectedLocation({
              lat,
              lon,
              address: preferredAddress || '',
              addressComponents: [],
              placeName: preferredAddress || '',
            })
          })
        }

        const placeMarker = position => {
          if (markerRef.current) markerRef.current.setMap(null)
          const marker = new window.google.maps.Marker({
            position,
            map: mapInstance,
            draggable: true,
          })
          markerRef.current = marker
          marker.addListener('dragend', e => {
            resolveLocation(e.latLng.lat(), e.latLng.lng())
          })
        }

        if (initialLat && initialLon) {
          placeMarker({ lat: initialLat, lng: initialLon })
          resolveLocation(initialLat, initialLon)
        }

        const autocompleteOptions = {
          fields: ['geometry', 'formatted_address', 'name', 'address_components'],
        }
        if (countryCode) {
          autocompleteOptions.componentRestrictions = { country: toLower(countryCode) }
        }

        const autocomplete = new window.google.maps.places.Autocomplete(searchRef.current, autocompleteOptions)
        autocomplete.bindTo('bounds', mapInstance)
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          if (!place.geometry || !place.geometry.location) return

          const lat = place.geometry.location.lat()
          const lon = place.geometry.location.lng()
          const inCountry = isLocationInCountry(place.address_components, countryCode)
          setOutOfCountry(!inCountry)
          if (!inCountry) {
            setSelectedLocation(null)
            setAddress('')
            if (markerRef.current) {
              markerRef.current.setMap(null)
              markerRef.current = null
            }
            notify.error(mapT('addCityModal.messages.locationOutsideCountry'))
            return
          }

          mapInstance.setCenter(place.geometry.location)
          mapInstance.setZoom(14)
          placeMarker(place.geometry.location)

          const newAddress = place.formatted_address || place.name || ''
          setAddress(newAddress)
          setSelectedLocation({
            lat,
            lon,
            address: newAddress,
            addressComponents: place.address_components || [],
            placeName: place.name || '',
          })
        })

        mapInstance.addListener('click', event => {
          placeMarker(event.latLng)
          resolveLocation(event.latLng.lat(), event.latLng.lng())
        })

        setTimeout(() => {
          if (cancelled) return
          window.google.maps.event.trigger(mapInstance, 'resize')
          if (countryView.bounds) {
            mapInstance.fitBounds(countryView.bounds)
          } else {
            mapInstance.setCenter(countryView.center)
          }
          setMapLoading(false)
        }, 350)
      })
      .catch(error => {
        console.error('Failed to load Google Maps:', error)
        if (!cancelled) {
          setMapLoading(false)
          notify.error(t('addCityModal.messages.failedToResolveLocation'))
        }
      })

    return () => {
      cancelled = true
    }
  }, [countryCode, mapLanguage, countryName, initialLat, initialLon, t])

  const confirmDisabled = !selectedLocation || outOfCountry || confirming || mapLoading

  const handleConfirmLocation = async () => {
    if (confirmDisabled || !selectedLocation) return

    setConfirming(true)
    try {
      if (countryCode && selectedLocation.addressComponents?.length && !isLocationInCountry(selectedLocation.addressComponents, countryCode)) {
        setOutOfCountry(true)
        notify.error(t('addCityModal.messages.locationOutsideCountry'))
        return
      }

      const { lat, lon } = selectedLocation

      // Always resolve BOTH languages independently. Never copy one language into the other field.
      const englishResult = await reverseGeocodeCityName(lat, lon, 'en')
      const arabicResult = await reverseGeocodeCityName(lat, lon, 'ar')

      const mapFallbackName =
        extractCityName(selectedLocation.addressComponents, { preferArabic: mapLanguage === 'ar' }) ||
        cityNameFromAddress(selectedLocation.address, { preferArabic: mapLanguage === 'ar' }) ||
        cityNameFromAddress(selectedLocation.address)

      let englishCity = englishResult.cityName || ''
      let arabicCity = arabicResult.cityName || ''

      // Prefer Arabic place name from Autocomplete when the Arabic map was used.
      if ((!arabicCity || !hasArabicScript(arabicCity)) && hasArabicScript(selectedLocation.placeName)) {
        arabicCity = selectedLocation.placeName
      }

      // Only use map selection as fallback for the matching language.
      if (!englishCity && mapLanguage === 'en') englishCity = mapFallbackName
      if ((!arabicCity || !hasArabicScript(arabicCity)) && mapLanguage === 'ar' && hasArabicScript(mapFallbackName)) {
        arabicCity = mapFallbackName
      }

      // If Arabic still Latin-only, force Maps JS geocode in Arabic.
      if (!arabicCity || !hasArabicScript(arabicCity)) {
        const arabicMapsResult = await reverseGeocodeViaMapsJs(lat, lon, 'ar')
        if (arabicMapsResult.cityName && hasArabicScript(arabicMapsResult.cityName)) {
          arabicCity = arabicMapsResult.cityName
        }
      }

      // Plus Code / Latin-only reverse geocodes are common in PK — look up Arabic by English name.
      if ((!arabicCity || !hasArabicScript(arabicCity)) && englishCity) {
        const lookedUpArabic = await resolveArabicCityNameFromEnglish(englishCity, countryName, countryCode)
        if (lookedUpArabic) arabicCity = lookedUpArabic
      }

      // Last resort: keep whatever Maps returned (may still be Latin for obscure places).
      if (!arabicCity) {
        arabicCity = arabicResult.cityName || ''
      }

      // If English empty, force Maps JS geocode in English.
      if (!englishCity) {
        const englishMapsResult = await reverseGeocodeViaMapsJs(lat, lon, 'en')
        if (englishMapsResult.cityName) englishCity = englishMapsResult.cityName
      }

      onLocationSelect({
        lat,
        lon,
        address: englishResult.address || selectedLocation.address,
        city: englishCity || cityNameFromAddress(selectedLocation.address) || selectedLocation.address,
        cityAr: arabicCity,
      })
    } catch (error) {
      console.error('Failed to confirm location:', error)
      notify.error(t('addCityModal.messages.failedToResolveLocation'))
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="w-full" style={{ height: '520px', display: 'flex', flexDirection: 'column' }}>
      <input
        type="text"
        ref={searchRef}
        dir={mapLanguage === 'ar' ? 'rtl' : 'ltr'}
        className={`w-full p-2 mb-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-dark-500 ${
          mapLanguage === 'ar' ? 'text-right' : 'text-left'
        }`}
        placeholder={mapT('common.placeholders.searchLocation') || 'Search location'}
      />
      <div className="relative w-full rounded-lg" style={{ flex: 1, minHeight: '340px' }}>
        <div ref={mapRef} className="w-full h-full rounded-lg" />
        {mapLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 text-sm text-gray-600 rounded-lg">
            {mapT('common.loading') || 'Loading map...'}
          </div>
        )}
      </div>

      {outOfCountry && (
        <div className={`mt-2 text-xs text-red-600 ${mapLanguage === 'ar' ? 'text-right' : ''}`}>
          {mapT('addCityModal.messages.locationOutsideCountry')}
          {countryName ? ` (${countryName})` : ''}
        </div>
      )}
      {address && !outOfCountry && (
        <div className="mt-1 text-sm text-gray-700 truncate" dir={mapLanguage === 'ar' ? 'rtl' : 'ltr'}>
          {address}
        </div>
      )}

      <div className="mt-3 w-full flex justify-between">
        <button type="button" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none" onClick={onClose}>
          {mapT('addCityModal.buttons.closeMap')}
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-dark-600 text-white rounded-md hover:bg-dark-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleConfirmLocation}
          disabled={confirmDisabled}
        >
          {confirming ? mapT('addCityModal.buttons.confirmingLocation') : mapT('addCityModal.buttons.confirmLocation')}
        </button>
      </div>
    </div>
  )
}

export default CityLocationPicker
